export const CRM_BREAKOUT_REQUEST = 'enrollio:crm-session-breakout-request';
export const CRM_BREAKOUT_PAYLOAD = 'enrollio:crm-session-breakout-payload';

export type CrmBreakoutSessionPayload = {
  message: typeof CRM_BREAKOUT_PAYLOAD;
  token: string;
  user: unknown;
  studio: unknown;
};

export function buildReturnToPath(
  loc: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location,
): string {
  return `${loc.pathname}${loc.search}${loc.hash}` || '/';
}

export function safeCrmBreakoutReturnPath(returnTo: string | undefined): string {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return '/';
  }
  return returnTo;
}

export function buildStudentDetailsReturnTo(
  studentId: string,
  locationId?: string | null,
): string {
  const id = String(locationId || '').trim();
  const base = `/students/${encodeURIComponent(studentId)}/details`;
  if (!id) {
    return base;
  }
  return `${base}?id=${encodeURIComponent(id)}`;
}

export function buildCrmSessionBreakoutHref(
  loc: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'> & {
    returnTo?: string;
    locationId?: string | null;
  } = window.location,
): string {
  const returnTo = safeCrmBreakoutReturnPath(
    loc.returnTo ?? buildReturnToPath(loc),
  );
  const params = new URLSearchParams({ returnTo });
  const locationId = String(loc.locationId || '').trim();
  if (locationId) {
    params.set('locationId', locationId);
  }
  return `${loc.origin}/crm-session-breakout?${params.toString()}`;
}

function isTrustedBreakoutRequest(
  event: MessageEvent,
  expectedOrigin: string,
  child: Window,
): boolean {
  return (
    event.origin === expectedOrigin &&
    event.source === child &&
    !!event.data &&
    event.data.message === CRM_BREAKOUT_REQUEST
  );
}

/**
 * Opens Studio outside the CRM iframe and hands auth across via window.opener.
 *
 * Chrome partitions localStorage for third-party iframes, so opening the portal
 * URL alone would land unauthenticated. Do not use noopener/noreferrer — the
 * breakout page needs window.opener for the same-origin postMessage handoff.
 */
export function openStandalonePortalWindow(params?: {
  open?: typeof window.open;
  loc?: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'> & {
    returnTo?: string;
    locationId?: string | null;
  };
  getPayload?: () => Omit<CrmBreakoutSessionPayload, 'message'> | null;
  addListener?: typeof window.addEventListener;
  removeListener?: typeof window.removeEventListener;
  schedule?: (fn: () => void, ms: number) => unknown;
}): Window | null {
  const open = params?.open ?? window.open.bind(window);
  const loc = params?.loc ?? window.location;
  const addListener = params?.addListener ?? window.addEventListener.bind(window);
  const removeListener =
    params?.removeListener ?? window.removeEventListener.bind(window);
  const schedule =
    params?.schedule ?? ((fn, ms) => window.setTimeout(fn, ms));
  const getPayload =
    params?.getPayload ??
    (() => {
      // Callers in UI pass stores. Default is null so unit tests stay free of zustand.
      return null;
    });

  const child = open(buildCrmSessionBreakoutHref(loc), '_blank');
  if (!child) {
    return null;
  }

  const expectedOrigin = loc.origin;
  const onMessage = (event: MessageEvent) => {
    if (!isTrustedBreakoutRequest(event, expectedOrigin, child)) {
      return;
    }
    const payload = getPayload();
    if (!payload?.token) {
      return;
    }
    child.postMessage(
      {
        message: CRM_BREAKOUT_PAYLOAD,
        token: payload.token,
        user: payload.user,
        studio: payload.studio,
      } satisfies CrmBreakoutSessionPayload,
      expectedOrigin,
    );
    removeListener('message', onMessage);
  };

  addListener('message', onMessage);
  // Drop the listener if the child never asks (closed / blocked mid-flight).
  schedule(() => {
    removeListener('message', onMessage);
  }, 30_000);

  return child;
}

export type CrmBreakoutNoOpenerDecision =
  | { action: 'continue'; path: string }
  | { action: 'need-crm-handoff'; path: string; locationId?: string }
  | { action: 'login'; locationId: string; redirect: string };

/**
 * ENR-4102: right-click "Open in new tab" does not set window.opener, so we
 * cannot postMessage the iframe session. Never continue under a *different*
 * top-level studio (that caused AUD currency + student 404s).
 */
export function decideCrmBreakoutWithoutOpener(params: {
  returnTo?: string;
  locationId?: string | null;
  token: string | null | undefined;
  studioLocationId: string | null | undefined;
}): CrmBreakoutNoOpenerDecision {
  const path = safeCrmBreakoutReturnPath(params.returnTo);
  const fromQuery = (() => {
    const qIndex = path.indexOf('?');
    if (qIndex < 0) {
      return '';
    }
    return (
      new URLSearchParams(path.slice(qIndex + 1)).get('id') || ''
    ).trim();
  })();
  const wanted = String(params.locationId || fromQuery || '').trim();
  const token = String(params.token || '').trim();
  const studioLocationId = String(params.studioLocationId || '').trim();

  if (token && wanted && studioLocationId === wanted) {
    return { action: 'continue', path };
  }

  if (token && wanted && studioLocationId && studioLocationId !== wanted) {
    return { action: 'need-crm-handoff', path, locationId: wanted };
  }

  if (!token && wanted) {
    return { action: 'login', locationId: wanted, redirect: path };
  }

  return {
    action: 'need-crm-handoff',
    path,
    locationId: wanted || undefined,
  };
}
